import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { ensureProductionFirebaseApp, type FirebaseEnv } from '@/libs/firebase/init';
import { captureException } from '@/libs/logger';
import { mmkvStorage } from '@/stores/middleware/mmkv-storage';

type FirebaseEnvState = {
  env: FirebaseEnv;
};

type FirebaseEnvActions = {
  setEnv: (env: FirebaseEnv) => void;
};

const triggerProductionInit = () => {
  ensureProductionFirebaseApp().catch(error => {
    captureException(
      { error_payload: error },
      { tags: { usk_log_type: 'firebase_app_init_error' } },
    );
  });
};

export const useFirebaseEnvStore = create<FirebaseEnvState & FirebaseEnvActions>()(
  persist(
    set => ({
      env: 'development',
      setEnv: env => {
        if (env === 'production') triggerProductionInit();
        set({ env });
      },
    }),
    {
      name: 'firebase-env-store',
      storage: createJSONStorage(() => mmkvStorage),
      onRehydrateStorage: () => state => {
        if (state?.env === 'production') triggerProductionInit();
      },
    },
  ),
);

/**
 * Non-hook accessor for use inside helpers that run outside React (e.g.
 * firebase auth helpers).
 */
export const getCurrentFirebaseEnv = (): FirebaseEnv => useFirebaseEnvStore.getState().env;
